import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CalendarViewWeekOutlinedIcon from '@mui/icons-material/CalendarViewWeekOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { InstallAppCard } from '../components/InstallAppCard';
import type { ActionCount } from '../lib/adminActions';
import { signOutCurrent } from '../services/firebase';
import { useAdminActionCounts } from '../services/useAdminActionCounts';
import { usePendingContractRequestCount } from '../services/useClients';
import { useStaffUnreadCount } from '../services/useMessaging';

import { LanguageSwitcherMui } from './LanguageSwitcherMui';
import { MessagesMenu } from './MessagesMenu';
import { NotificationBell } from './NotificationBell';
import {
  shellActionsSpacing,
  shellContentSx,
  shellDrawerPaperSx,
  shellHeaderSx,
  shellMainSx,
  shellRootSx,
  shellTitleSx,
} from './shellStyles';

const DRAWER_WIDTH = 248;

/**
 * Sidebar is grouped into four sections, separated by section labels:
 *   1. Operatiivne — daily ops: dashboard + the four core entities
 *   2. Rahandus    — money flow: invoices, reports, pricing
 *   3. Haldus      — admin/config: users, taxonomies (skills), settings, audit
 *   4. Arendused   — feature requests for the team itself
 *
 * Operatiivne has no header label (it's the unlabelled top of the list).
 * Each subsequent group renders its `sectionKey` heading.
 */
const NAV_SECTIONS = [
  {
    sectionKey: null as string | null,
    items: [
      { to: '/', key: 'dashboard', Icon: DashboardOutlinedIcon },
      { to: '/live', key: 'live', Icon: SensorsOutlinedIcon },
      { to: '/jobs', key: 'jobs', Icon: WorkOutlineOutlinedIcon },
      { to: '/schedule', key: 'schedule', Icon: CalendarViewWeekOutlinedIcon },
      { to: '/timesheets', key: 'timesheets', Icon: ScheduleOutlinedIcon },
      { to: '/workers', key: 'workers', Icon: PeopleAltOutlinedIcon },
      { to: '/leave-requests', key: 'leaveRequests', Icon: BeachAccessOutlinedIcon },
      { to: '/messages', key: 'messages', Icon: ChatBubbleOutlineOutlinedIcon },
      { to: '/partners', key: 'partners', Icon: HandshakeOutlinedIcon },
      { to: '/clients', key: 'clients', Icon: BusinessOutlinedIcon },
    ],
  },
  {
    sectionKey: 'finance' as string | null,
    items: [
      { to: '/invoices', key: 'invoices', Icon: ReceiptLongOutlinedIcon },
      { to: '/partner-invoices', key: 'partnerInvoices', Icon: HandshakeOutlinedIcon },
      { to: '/reports', key: 'reports', Icon: AssessmentOutlinedIcon },
    ],
  },
  {
    sectionKey: 'admin' as string | null,
    items: [
      { to: '/users', key: 'users', Icon: PersonOutlineOutlinedIcon },
      { to: '/settings', key: 'settings', Icon: SettingsOutlinedIcon },
    ],
  },
] as const;

// Development-aux items shown under a separate Arendused header at the
// bottom. Tegevuskava (/roadmap) is intentionally hidden; Soovid stays.
const DEVELOPMENT_NAV_ITEMS = [
  { to: '/feature-requests', key: 'featureRequests', Icon: LightbulbOutlinedIcon },
] as const;

// Reachable-but-hidden routes (kept so getPageKey highlights the right
// state when user navigates directly — e.g. /roadmap is still a valid
// bookmark, just not in the nav).
const HIDDEN_NAV_ITEMS = [
  { to: '/roadmap', key: 'roadmap', Icon: TimelineOutlinedIcon },
] as const;

type NavItem = { to: string; key: string };

const ALL_NAV_ITEMS: NavItem[] = [
  ...NAV_SECTIONS.flatMap((s) => s.items as readonly NavItem[]),
  ...(DEVELOPMENT_NAV_ITEMS as readonly NavItem[]),
  ...(HIDDEN_NAV_ITEMS as readonly NavItem[]),
];

function getPageKey(pathname: string): string {
  if (pathname === '/') return 'dashboard';
  const match = ALL_NAV_ITEMS.find(
    (item) => item.to !== '/' && pathname.startsWith(item.to),
  );
  return match?.key ?? 'dashboard';
}

/**
 * The number beside a menu name: what needs RM24 there now. Red when
 * something is due or overdue, grey when it is to do but not urgent; nothing
 * at zero, so an empty menu reads as "all clear".
 */
function NavCount({ label, action }: { label: string; action?: ActionCount }) {
  const { t } = useTranslation('admin');
  if (!action || action.count <= 0) return null;
  return (
    <Chip
      size="small"
      label={action.count > 99 ? '99+' : action.count}
      color={action.urgent ? 'error' : 'default'}
      aria-label={t('admin:nav.actionCount', { count: action.count, section: label })}
      sx={{ height: 20, fontSize: 12, fontWeight: 600, ml: 1 }}
    />
  );
}

export function AdminShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['admin', 'common']);
  const pendingContractRequests = usePendingContractRequestCount();
  const messagesUnread = useStaffUnreadCount();
  const actions = useAdminActionCounts();
  // What needs RM24 now, beside each menu name (Marti 2026-10-28). An unread
  // message and a contract request are always someone waiting on RM24.
  const navActions = {
    jobs: actions.jobs,
    timesheets: actions.timesheets,
    leaveRequests: actions.leaveRequests,
    messages: { count: messagesUnread, urgent: true },
    clients: { count: pendingContractRequests, urgent: true },
  };
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeKey = getPageKey(location.pathname);
  const displayName = user?.displayName ?? user?.email ?? '';
  const initials = displayName
    .split(/[\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  const sidebar = (
    <Box
      role="navigation"
      sx={{
        width: DRAWER_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          component="img"
          src="/rm24_logo.jpg"
          alt={t('common:app.name')}
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            objectFit: 'cover',
          }}
        />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
          {t('common:app.name')}
        </Typography>
      </Box>
      <Divider sx={{ mx: 1 }} />
      <List sx={{ py: 1, flexGrow: 1 }}>
        {NAV_SECTIONS.map((section, sectionIdx) => (
          <Box key={section.sectionKey ?? `_top-${sectionIdx}`}>
            {section.sectionKey && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 2.5,
                  pt: 2,
                  pb: 0.5,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  fontWeight: 600,
                }}
              >
                {t(`admin:nav.sections.${section.sectionKey}`)}
              </Typography>
            )}
            {section.items.map(({ to, key, Icon }) => (
              <ListItemButton
                key={to}
                component={NavLink}
                to={to}
                selected={activeKey === key}
                onClick={() => setMobileOpen(false)}
              >
                <ListItemIcon>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t(`admin:nav.${key}`)}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: activeKey === key ? 600 : 500,
                  }}
                />
                <NavCount
                  label={t(`admin:nav.${key}`)}
                  action={navActions[key as keyof typeof navActions]}
                />
              </ListItemButton>
            ))}
          </Box>
        ))}

        {DEVELOPMENT_NAV_ITEMS.length > 0 && (
          <>
            <Divider sx={{ mx: 1, my: 1.5 }} />
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 2.5,
                pb: 0.5,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontWeight: 600,
                fontStyle: 'italic',
              }}
            >
              {t('admin:nav.sections.development')}
            </Typography>
          </>
        )}
        {DEVELOPMENT_NAV_ITEMS.map(({ to, key, Icon }) => (
          <ListItemButton
            key={to}
            component={NavLink}
            to={to}
            selected={activeKey === key}
            onClick={() => setMobileOpen(false)}
            sx={{ color: 'grey.700' }}
          >
            <ListItemIcon sx={{ color: 'grey.700' }}>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t(`admin:nav.${key}`)}
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: activeKey === key ? 600 : 500,
                fontStyle: 'italic',
                color: 'grey.700',
              }}
            />
          </ListItemButton>
        ))}
      </List>
      <Divider sx={{ mx: 1 }} />
      <Box sx={{ p: 1 }}>
        <ListItemButton onClick={() => void signOutCurrent()}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t('common:actions.signOut')}
            primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={shellRootSx}>
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            // Kept mounted for speed, but a CLOSED modal drawer must not take
            // part in layout: MUI only hides it with `visibility`, so its box
            // stayed and stretched the document to 540px on a 393px phone.
            // Everything then scrolled sideways and dialogs opened half off
            // the screen. Found by the mobile pass of the browser suite.
            '&.MuiModal-hidden': { display: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', ...shellDrawerPaperSx },
          }}
        >
          {sidebar}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', ...shellDrawerPaperSx },
          }}
        >
          {sidebar}
        </Drawer>
      </Box>

      <Box
        sx={shellContentSx(DRAWER_WIDTH)}
      >
        <Toolbar
          disableGutters
          sx={shellHeaderSx}
        >
          <IconButton
            edge="start"
            onClick={() => setMobileOpen((v) => !v)}
            sx={{ mr: 1.5, display: { md: 'none' } }}
            aria-label="menu"
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h2" noWrap sx={shellTitleSx}>
            {t(`admin:pageTitles.${activeKey}`)}
          </Typography>

          <Stack direction="row" spacing={shellActionsSpacing} alignItems="center">
            <LanguageSwitcherMui />
            <MessagesMenu variant="staff" />
            <NotificationBell />
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ pl: 1 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {initials || 'A'}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1.2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('admin:header.adminRole')}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Toolbar>

        <Box component="main" sx={shellMainSx}>
          <InstallAppCard />
          {children}
        </Box>
      </Box>
    </Box>
  );
}
